import {useEffect, useRef} from "react";
import type {ElementType, MouseEvent as ReactMouseEvent, Ref} from "react";
import type {MathfieldElement} from "mathlive";
// Side-effect import: registers the <math-field> custom element globally.
// Safe to import even when `type` isn't "math"/"smart" — it just defines the
// element, it doesn't render one.
import "mathlive";
// Side-effect import: registers KaTeX's \ce{} chemistry macro globally, so
// mhchem notation typed in the "smart" editor still renders correctly
// wherever a saved answer is displayed read-only later (Problem's answer
// display, GradeResult notes) — those go through KaTeX, not MathLive, and
// KaTeX doesn't know \ce{} without this.
import "katex/contrib/mhchem";
import Editor, {useMonaco} from "@monaco-editor/react";
import {useTheme} from "../../hooks/useTheme";
import "./AnswerEditor.css";

// @monaco-editor/react's default CDN loader (jsdelivr) is the
// officially-supported zero-config path — it ships a complete prebuilt
// bundle with worker loading that Just Works. A local-bundling alternative
// (pointing loader.config at the npm `monaco-editor` package) was tried
// first to avoid the CDN dependency, but monaco-editor 0.56's workers use a
// `new URL(..., import.meta.url)` pattern that CRA's dev server (webpack 5,
// non-ejected) can't serve correctly — it 404s and the SPA fallback serves
// index.html instead, crashing the editor. Fixing that properly needs
// `monaco-editor-webpack-plugin`, which isn't installable without ejecting
// or adding a tool like craco. Sticking with the CDN loader until/unless
// that trade-off is revisited.
//
// KNOWN DEV-ONLY COSMETIC ISSUE (silenced): React 18 StrictMode
// double-mounts every component once in dev (mount → cleanup → mount) to
// surface effect bugs, which races the loader's cancelable init promise —
// the first, cancelled attempt rejects with `{type: "cancelation"}` on a
// promise chain internal to the library. A .catch() or
// `unhandledrejection` listener from *this* module can't suppress it, since
// CRA's dev-server error overlay (react-error-overlay) registers its own
// `unhandledrejection` listener as soon as its bundle runs, ahead of any of
// our component code. The actual fix lives in public/index.html: an
// early `<head>` script registers a listener for this exact rejection
// shape before the dev-client bundle (injected later, into `<body>`) ever
// loads, so it sees the event first and calls stopImmediatePropagation()
// to keep the overlay from showing it. Cosmetic either way — dismissing
// the overlay always revealed a fully working, correctly themed editor
// underneath — and it never happens in production (StrictMode doesn't
// double-mount there).

// mathlive ships the runtime custom element but no JSX typing for it, and
// CRA's fork-ts-checker (unlike plain tsc) doesn't reliably pick up a
// `declare global { namespace JSX {...} }` augmentation file for an element
// that's otherwise unimported — so `<math-field>` fails TS2339 in the dev
// build even though the exact same code type-checks fine with `tsc
// --noEmit`. Routing through an untyped ElementType alias sidesteps
// JSX.IntrinsicElements lookup entirely instead of fighting the checker.
const MathField = "math-field" as unknown as ElementType<{
    ref?: Ref<MathfieldElement>;
    id?: string;
    className?: string;
}>;

/**
 * Which widget renders the answer field — set per-problem by the backend
 * (e.g. `data.answerFormat` from getProblemMeta) so a node about evaluating
 * an integral can open a live math editor while a proof-writing node opens
 * plain text. Unrecognized/missing values should fall back to "text".
 *
 * "smart" is the general-purpose one: ordinary typed prose with buttons
 * that insert small, independently-editable MathLive fields inline —
 * for problems that mix explanation with notation (chemistry equations,
 * a couple of integrals inside a written argument, etc.) rather than being
 * *only* prose ("text") or *only* one expression ("math").
 */
export type AnswerEditorType = "text" | "code" | "math" | "smart";

export interface SmartButton {
    label: string;
    /** Raw LaTeX inserted as a new inline math field's initial content. */
    insert: string;
    title: string;
}

// Raw LaTeX commands — no `$...$` wrapping needed, math-field content is
// always LaTeX. Each snippet's first empty `{}` becomes the field's
// selection after insert (MathLive's default `selectionMode: "placeholder"`),
// so the user can start typing the argument immediately.
const LATEX_SNIPPETS: { label: string; insert: string; title: string }[] = [
    {label: "√", insert: "\\sqrt{}", title: "Square root"},
    {label: "x/y", insert: "\\frac{}{}", title: "Fraction"},
    {label: "xⁿ", insert: "x^{}", title: "Exponent"},
    {label: "xₙ", insert: "x_{}", title: "Subscript"},
    {label: "Σ", insert: "\\sum_{}^{}", title: "Summation"},
    {label: "∫", insert: "\\int_{}^{}", title: "Integral"},
    {label: "π", insert: "\\pi", title: "Pi"},
    {label: "∞", insert: "\\infty", title: "Infinity"},
    {label: "≤", insert: "\\leq", title: "Less than or equal"},
    {label: "≥", insert: "\\geq", title: "Greater than or equal"},
];

// Placeholder default until the backend sends per-problem buttons (see
// SmartButton / notes at the bottom of this file) — a generic set covering
// the same symbols as the pure-math editor, plus one chemistry example
// (\ce{} is mhchem notation, registered globally by the katex/contrib/mhchem
// import above; MathLive supports it natively for the live inline field).
const DEFAULT_SMART_BUTTONS: SmartButton[] = [
    ...LATEX_SNIPPETS,
    {label: "⚗", insert: "\\ce{}", title: "Chemical equation"},
];

const DEFAULT_CODE_LANGUAGE = "plaintext";

function cssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Monaco's theme is a static object registered once via `defineTheme`, not
 * something that reacts to CSS on its own — so to actually track the site's
 * light/dark toggle we re-read the (already-updated, since this runs off
 * the same `theme` value that drove the [data-theme] attribute) CSS custom
 * properties and re-register the theme every time `theme` changes, then
 * re-apply it. Colors come from the real tokens (not hardcoded hex) so a
 * future palette tweak in tokens.css doesn't also need updating here.
 */
function useEolassMonacoTheme() {
    const {theme} = useTheme();
    const monaco = useMonaco();

    useEffect(() => {
        if (!monaco) return;

        monaco.editor.defineTheme("eolass", {
            base: theme === "dark" ? "vs-dark" : "vs",
            inherit: true,
            rules: [],
            colors: {
                "editor.background": cssVar("--paper"),
                "editor.foreground": cssVar("--text-on-paper"),
                "editorLineNumber.foreground": cssVar("--text-on-paper-soft"),
                "editorLineNumber.activeForeground": cssVar("--text-on-paper"),
                "editorCursor.foreground": cssVar("--jade-deep"),
                "editor.lineHighlightBackground": cssVar("--paper-dim"),
                "editor.lineHighlightBorder": cssVar("--paper-dim"),
                "editorWidget.background": cssVar("--paper-dim"),
                "editorWidget.border": cssVar("--line"),
                "editorSuggestWidget.background": cssVar("--paper-dim"),
                "editorSuggestWidget.border": cssVar("--line"),
            },
        });
        monaco.editor.setTheme("eolass");
    }, [monaco, theme]);
}

function CodeAnswerField({value, onChange, language, id}: {
    value: string;
    onChange: (value: string) => void;
    language: string;
    id: string;
}) {
    useEolassMonacoTheme();

    return (
        <div className="answer-editor__code">
            <Editor
                value={value}
                onChange={(next) => onChange(next ?? "")}
                language={language}
                theme="eolass"
                wrapperProps={{id}}
                loading={<span className="answer-editor__code-loading">Loading editor…</span>}
                options={{
                    minimap: {enabled: false},
                    fontSize: 14,
                    fontFamily: "var(--font-mono)",
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    tabSize: 2,
                    padding: {top: 12, bottom: 12},
                }}
            />
        </div>
    );
}

// ---------------------------------------------------------------------------
// "smart" editor
//
// Ordinary editable text with small inline MathLive fields mixed in.
//
// IMPORTANT ARCHITECTURE NOTE: math fields are NOT descendants of a native
// contentEditable region here. An earlier version nested <math-field>
// elements inside one big contentEditable div; that looked right but wasn't
// actually usable — confirmed by hand: clicking directly on a rendered
// integral and typing sent the keystroke to the end of the whole paragraph,
// not into the field. Browsers don't reliably delegate focus/keyboard input
// into a focusable custom element that's a *descendant* of a
// contentEditable ancestor — the ancestor's own native editing engine
// intercepts it. This is a known limitation, and the standard fix (used by
// real rich-text editors like ProseMirror/Lexical for "atomic inline
// nodes") is what's implemented below: each plain-text run is its own small
// independently-editable <span contenteditable>, and math fields sit
// between them as ordinary DOM *siblings*, never nested inside an editable
// ancestor. Navigating and deleting across the boundary between a text run
// and a field is wired up explicitly (arrow keys, backspace/delete,
// MathLive's own "move-out" event) rather than relying on native browser
// behavior, since there's no single contentEditable region for the browser
// to manage that for us.
// ---------------------------------------------------------------------------

function isMathFieldEl(node: Node | null): node is MathfieldElement {
    return !!node && node instanceof HTMLElement && node.tagName === "MATH-FIELD";
}

const SMART_TEXT_CLASS = "answer-editor__smart-text";

function isTextSpanEl(node: Node | null): node is HTMLElement {
    return !!node && node instanceof HTMLElement && node.classList.contains(SMART_TEXT_CLASS);
}

/**
 * Reads the editor's actual DOM back into the plain string we store/submit
 * — each text span as its textContent, each inline math field as `$latex$`.
 * This is deliberately the same inline-math convention `remark-math`/
 * `rehype-katex` already use everywhere a saved answer gets displayed
 * read-only, so nothing downstream needs a new parser for it.
 *
 * Known limitation: a literal "$" typed as plain text (e.g. "costs $5")
 * isn't escaped, so it can misalign this delimiter-based scheme on the next
 * external rebuild. Same trade-off the intro/problem split already made —
 * flagged here rather than solved, since escaping would need a real
 * grammar. See the notes at the bottom of this file.
 */
function serializeSmartContent(container: HTMLElement): string {
    let out = "";
    for (const child of Array.from(container.children)) {
        out += isMathFieldEl(child) ? `$${child.value}$` : (child.textContent ?? "");
    }
    return out;
}

/** Caret offset (characters) within `span`'s own text, derived from a Selection Range. */
function caretOffsetWithin(span: HTMLElement, range: Range): number {
    const probe = range.cloneRange();
    probe.selectNodeContents(span);
    probe.setEnd(range.startContainer, range.startOffset);
    return probe.toString().length;
}

function setCaretOffsetWithin(span: HTMLElement, offset: number) {
    const selection = window.getSelection();
    if (!selection) return;
    const textNode = span.firstChild;
    const node = textNode && textNode.nodeType === Node.TEXT_NODE ? textNode : span;
    const max = node.textContent?.length ?? 0;
    const range = document.createRange();
    range.setStart(node, Math.min(Math.max(offset, 0), max));
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
}

type MoveOutDirection = "forward" | "backward" | "upward" | "downward";

interface SmartFieldHandlers {
    onChange: () => void;
    /** Focus moved past the field's own start/end via arrow keys — MathLive's own event for exactly this "embedded in a larger document" case. */
    onMoveOut: (mf: MathfieldElement, direction: MoveOutDirection) => void;
}

interface SmartSpanHandlers {
    onChange: () => void;
    onEnterAdjacentField: (span: HTMLElement, direction: "next" | "previous") => void;
}

function createInlineMathField(latex: string, handlers: SmartFieldHandlers): MathfieldElement {
    const mf = document.createElement("math-field") as unknown as MathfieldElement;
    mf.value = latex;
    mf.className = "answer-editor__inline-mathfield";
    mf.addEventListener("input", handlers.onChange);
    mf.addEventListener("move-out", (e) => {
        e.preventDefault();
        handlers.onMoveOut(mf, (e as CustomEvent<{direction: MoveOutDirection}>).detail.direction);
    });
    return mf;
}

function createTextSpan(text: string, handlers: SmartSpanHandlers): HTMLElement {
    const span = document.createElement("span");
    span.className = SMART_TEXT_CLASS;
    span.contentEditable = "true";
    span.textContent = text;

    span.addEventListener("input", handlers.onChange);

    // Forces plain text on paste — otherwise the browser's default rich
    // paste can inject arbitrary HTML into a single text run that
    // serializeSmartContent has no model for.
    span.addEventListener("paste", (e) => {
        e.preventDefault();
        document.execCommand("insertText", false, e.clipboardData?.getData("text/plain") ?? "");
    });

    span.addEventListener("keydown", (e) => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        const range = selection.getRangeAt(0);
        const collapsed = range.collapsed;
        const offset = caretOffsetWithin(span, range);
        const text = span.textContent ?? "";

        // contentEditable's native Enter behavior varies by browser (a new
        // <div>-wrapped "paragraph" in Chrome, a <br> in Firefox) — neither
        // matches serializeSmartContent's plain-text model, so Enter is
        // handled manually: insert a literal "\n", which `white-space:
        // pre-wrap` (AnswerEditor.css) renders as a real line break and
        // which round-trips through serialize/rebuild without special-casing.
        if (e.key === "Enter") {
            e.preventDefault();
            const before = text.slice(0, offset);
            const after = text.slice(offset);
            span.textContent = before + "\n" + after;
            setCaretOffsetWithin(span, before.length + 1);
            handlers.onChange();
            return;
        }

        if (e.key === "ArrowRight" && collapsed && offset === text.length && isMathFieldEl(span.nextElementSibling)) {
            e.preventDefault();
            handlers.onEnterAdjacentField(span, "next");
            return;
        }
        if (e.key === "ArrowLeft" && collapsed && offset === 0 && isMathFieldEl(span.previousElementSibling)) {
            e.preventDefault();
            handlers.onEnterAdjacentField(span, "previous");
            return;
        }
        // Backspace/Delete adjacent to a math field deletes the *field*
        // (one keystroke, like deleting any other atomic character) rather
        // than doing nothing — matches how deleting an emoji or an
        // attachment chip works in other editors.
        if (e.key === "Backspace" && collapsed && offset === 0 && isMathFieldEl(span.previousElementSibling)) {
            e.preventDefault();
            span.previousElementSibling!.remove();
            handlers.onChange();
            return;
        }
        if (e.key === "Delete" && collapsed && offset === text.length && isMathFieldEl(span.nextElementSibling)) {
            e.preventDefault();
            span.nextElementSibling!.remove();
            handlers.onChange();
        }
    });

    return span;
}

/**
 * Inverse of serializeSmartContent — rebuilds DOM children from a stored
 * string. Always starts and ends with a text span (even if empty) so
 * there's always somewhere to click/type before the first and after the
 * last math field, and so the Backspace/Delete-adjacent-to-a-field logic
 * above never has to handle "there's no span here at all".
 */
function buildSmartChildren(value: string, spanHandlers: SmartSpanHandlers, fieldHandlers: SmartFieldHandlers): HTMLElement[] {
    const parts = value.split(/(\$[^$]*\$)/g).filter((p) => p !== "");
    const nodes: HTMLElement[] = [];
    let lastWasField = false;

    for (const part of parts) {
        const isMath = part.length >= 2 && part.startsWith("$") && part.endsWith("$");
        if (isMath) {
            nodes.push(createInlineMathField(part.slice(1, -1), fieldHandlers));
            lastWasField = true;
        } else {
            nodes.push(createTextSpan(part, spanHandlers));
            lastWasField = false;
        }
    }
    if (nodes.length === 0 || lastWasField) nodes.push(createTextSpan("", spanHandlers));
    if (isMathFieldEl(nodes[0])) nodes.unshift(createTextSpan("", spanHandlers));
    return nodes;
}

function SmartAnswerField({value, onChange, buttons, id, placeholder}: {
    value: string;
    onChange: (value: string) => void;
    buttons: SmartButton[];
    id: string;
    placeholder?: string;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    function emitChange() {
        const el = containerRef.current;
        if (!el) return;
        const next = serializeSmartContent(el);
        el.dataset.empty = next === "" ? "true" : "false";
        onChangeRef.current(next);
    }

    function handleMoveOut(mf: MathfieldElement, direction: MoveOutDirection) {
        const target = direction === "forward" ? mf.nextElementSibling : direction === "backward" ? mf.previousElementSibling : null;
        if (!isTextSpanEl(target)) return;
        target.focus();
        setCaretOffsetWithin(target, direction === "forward" ? 0 : (target.textContent?.length ?? 0));
    }

    function focusFieldNeighbor(span: HTMLElement, direction: "next" | "previous") {
        const neighbor = direction === "next" ? span.nextElementSibling : span.previousElementSibling;
        if (isMathFieldEl(neighbor)) neighbor.focus();
    }

    const spanHandlers: SmartSpanHandlers = {onChange: emitChange, onEnterAdjacentField: focusFieldNeighbor};
    const fieldHandlers: SmartFieldHandlers = {onChange: emitChange, onMoveOut: handleMoveOut};

    // Rebuild the DOM only when `value` changed from *outside* (switching
    // problems, initial mount) — comparing against a fresh serialization
    // means our own onChange calls (which set state to exactly what's
    // already in the DOM) never trigger a rebuild, so normal typing never
    // fights the caret/selection. The `el.children.length > 0` half of the
    // guard matters on first mount specifically: a brand-new empty problem
    // starts with value === "", which trivially matches an as-yet-childless
    // container's serialization ("" === "") — without checking child count
    // too, that reads as "nothing changed" and skips building the empty
    // text span the user needs somewhere to click into.
    useEffect(() => {
        const el = containerRef.current;
        if (!el || (el.children.length > 0 && serializeSmartContent(el) === value)) return;

        el.innerHTML = "";
        el.dataset.empty = value === "" ? "true" : "false";
        for (const node of buildSmartChildren(value, spanHandlers, fieldHandlers)) {
            el.appendChild(node);
        }
        // spanHandlers/fieldHandlers are fresh objects every render but
        // stable in *behavior* (they close over refs/functions, not props
        // directly) — only `value` should trigger a rebuild.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    function insertMathAtCaret(snippet: string) {
        const el = containerRef.current;
        if (!el) return;

        const active = document.activeElement;
        let targetSpan: HTMLElement | null = null;
        let offset = 0;

        if (isTextSpanEl(active) && el.contains(active)) {
            targetSpan = active;
            const selection = window.getSelection();
            offset =
                selection && selection.rangeCount > 0
                    ? caretOffsetWithin(active, selection.getRangeAt(0))
                    : (active.textContent?.length ?? 0);
        } else if (isTextSpanEl(el.lastElementChild)) {
            targetSpan = el.lastElementChild;
            offset = targetSpan.textContent?.length ?? 0;
        }
        if (!targetSpan) return;

        const text = targetSpan.textContent ?? "";
        const before = createTextSpan(text.slice(0, offset), spanHandlers);
        const mf = createInlineMathField(snippet, fieldHandlers);
        const after = createTextSpan(text.slice(offset), spanHandlers);

        targetSpan.replaceWith(before, mf, after);
        el.dataset.empty = "false";

        // Same placeholder-selection behavior as the pure-math editor:
        // focusing the field puts the caret in its first empty "{}".
        requestAnimationFrame(() => mf.focus());

        emitChange();
    }

    // Clicking in the padding around/below the text (not on any span or
    // field directly) should still focus the editor, same as clicking
    // below the last line of a textarea — without this, that empty area
    // is dead space since the container itself isn't editable.
    function handleContainerClick(e: ReactMouseEvent<HTMLDivElement>) {
        if (e.target !== e.currentTarget) return;
        const last = containerRef.current?.lastElementChild ?? null;
        if (isTextSpanEl(last)) {
            last.focus();
            setCaretOffsetWithin(last, last.textContent?.length ?? 0);
        }
    }

    return (
        <>
            <div className="answer-editor__toolbar" role="toolbar" aria-label="Insert math">
                {buttons.map(({label, insert, title}) => (
                    <button
                        key={label}
                        type="button"
                        className="answer-editor__latex-button"
                        title={title}
                        aria-label={title}
                        // mousedown (not click) fires before the focused
                        // text span loses its selection to the button.
                        onMouseDown={(e) => {
                            e.preventDefault();
                            insertMathAtCaret(insert);
                        }}
                    >
                        {label}
                    </button>
                ))}
            </div>
            {/* Not itself contentEditable — see the architecture note above
                buildSmartChildren. Each text run is its own independently-
                editable sibling <span>; math fields sit between them, never
                nested inside an editable ancestor. */}
            <div
                id={id}
                ref={containerRef}
                className="answer-editor__smart"
                data-placeholder={placeholder}
                onClick={handleContainerClick}
            />
        </>
    );
}

interface AnswerEditorProps {
    type: AnswerEditorType;
    value: string;
    onChange: (value: string) => void;
    id?: string;
    placeholder?: string;
    /** Monaco language id (e.g. "python", "javascript") — only used when type is "code". */
    language?: string;
    /** Insert-math buttons for the "smart" editor — falls back to a generic default set. */
    smartButtons?: SmartButton[];
}

export default function AnswerEditor({
    type,
    value,
    onChange,
    id = "answer",
    placeholder,
    language = DEFAULT_CODE_LANGUAGE,
    smartButtons = DEFAULT_SMART_BUTTONS,
}: AnswerEditorProps) {
    const mathFieldRef = useRef<MathfieldElement>(null);

    // <math-field> is an uncontrolled custom element (there's no React
    // "value" prop wiring for it) — push external changes in imperatively,
    // but only when they didn't originate from the field itself (guarded by
    // the !== check) so we don't fight the user's cursor mid-keystroke.
    useEffect(() => {
        const mf = mathFieldRef.current;
        if (mf && mf.value !== value) {
            mf.value = value;
        }
    }, [value]);

    useEffect(() => {
        const mf = mathFieldRef.current;
        if (!mf) return;

        const handleInput = () => onChange(mf.value);
        mf.addEventListener("input", handleInput);
        return () => mf.removeEventListener("input", handleInput);
    }, [onChange]);

    function insertLatex(snippet: string) {
        mathFieldRef.current?.insert(snippet, {focus: true});
    }

    return (
        <div className="answer-editor">
            {type === "math" && (
                <div className="answer-editor__toolbar" role="toolbar" aria-label="Insert LaTeX">
                    {LATEX_SNIPPETS.map(({label, insert, title}) => (
                        <button
                            key={label}
                            type="button"
                            className="answer-editor__latex-button"
                            title={title}
                            aria-label={title}
                            // mousedown (not click) fires before the math-field
                            // loses focus/selection to the button.
                            onMouseDown={(e) => {
                                e.preventDefault();
                                insertLatex(insert);
                            }}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {type === "math" ? (
                <MathField
                    ref={mathFieldRef}
                    id={id}
                    className="answer-editor__mathfield"
                />
            ) : type === "code" ? (
                <CodeAnswerField value={value} onChange={onChange} language={language} id={id} />
            ) : type === "smart" ? (
                <SmartAnswerField
                    value={value}
                    onChange={onChange}
                    buttons={smartButtons}
                    id={id}
                    placeholder={placeholder}
                />
            ) : (
                <textarea
                    id={id}
                    className="answer-editor__textarea"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                />
            )}
        </div>
    );
}
